import React, { useState, useEffect } from 'react';
import { goBackOrHome } from '@/lib/navHelpers';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '@/hooks/useAnalytics';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Play, Download, BookOpen, Clock, Star, User, Video, FileText, ArrowLeft, Home } from "lucide-react";
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Course {
  id: string;
  title: string;
  description: string;
  category: string;
  video_url: string | null;
  workbook_url: string | null;
  thumbnail_url: string | null;
  duration_minutes: number | null;
  difficulty_level: string;
  tags: string[];
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

const CourseLibrary = () => {
  const navigate = useNavigate();
  const { trackPageView, trackContentInteraction, trackFeatureUsage } = useAnalytics();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadPublishedCourses();
    trackPageView('course_library');
  }, []);

  const handleCourseInteraction = (course: Course, action: string) => {
    trackContentInteraction('course', course.id, action, {
      course_title: course.title,
      course_category: course.category,
      course_difficulty: course.difficulty_level
    });
  };

  const loadPublishedCourses = async () => {
    try {
      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .eq('is_published', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCourses(data || []);
    } catch (error) {
      console.error('Error loading courses:', error);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = courses.filter(course => {
    const matchesCategory = selectedCategory === 'all' || course.category === selectedCategory;
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const categories = ['all', ...Array.from(new Set(courses.map(course => course.category)))];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-6 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Navigation */}
          <div className="flex items-center gap-2 mb-6">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate(-1)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => goBackOrHome(navigate)}
              className="flex items-center gap-2"
            >
              <Home className="h-4 w-4" />
              Home
            </Button>
          </div>

          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <BookOpen className="h-8 w-8 text-primary" />
              <h1 className="text-4xl font-bold text-foreground">Course Library</h1>
            </div>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Discover comprehensive wellness courses designed to support your reflection journey with expert guidance and practical resources.
            </p>
          </div>

          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search courses..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  if (e.target.value) {
                    trackFeatureUsage('course_library', 'search', { query: e.target.value });
                  }
                }}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setSelectedCategory(category);
                    trackFeatureUsage('course_library', 'filter_category', { category });
                  }}
                  className="capitalize"
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>

          {/* Courses Grid */}
          {filteredCourses.length === 0 ? (
            <Card className="p-12 text-center">
              <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No courses found</h3>
              <p className="text-muted-foreground">
                {searchTerm || selectedCategory !== 'all' 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Check back soon for new wellness courses.'}
              </p>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCourses.map((course) => (
                <Card key={course.id} className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  {/* Course Thumbnail */}
                  <div className="aspect-video bg-gradient-to-br from-primary/10 to-primary/20 flex items-center justify-center">
                    {course.thumbnail_url ? (
                      <img 
                        src={course.thumbnail_url} 
                        alt={course.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-center">
                        <Video className="h-12 w-12 text-primary mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">Course Video</p>
                      </div>
                    )}
                  </div>

                  <div className="p-6">
                    {/* Course Info */}
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-2">{course.title}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-3 mb-3">{course.description}</p>
                      
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <Badge variant="secondary">{course.category}</Badge>
                        <Badge variant="outline">{course.difficulty_level}</Badge>
                        {course.duration_minutes && (
                          <Badge variant="outline" className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {course.duration_minutes}m
                          </Badge>
                        )}
                      </div>

                      {course.tags.length > 0 && (
                        <div className="flex items-center gap-1 flex-wrap">
                          {course.tags.slice(0, 3).map((tag, index) => (
                            <span key={index} className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                              {tag}
                            </span>
                          ))}
                          {course.tags.length > 3 && (
                            <span className="text-xs text-muted-foreground">+{course.tags.length - 3} more</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-2">
                      {course.video_url && (
                        <Button asChild className="w-full">
                          <a 
                            href={course.video_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            onClick={() => handleCourseInteraction(course, 'video_watched')}
                          >
                            <Play className="h-4 w-4 mr-2" />
                            Watch Course
                          </a>
                        </Button>
                      )}
                      
                      {course.workbook_url && (
                        <Button variant="outline" asChild className="w-full">
                          <a 
                            href={course.workbook_url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            download
                            onClick={() => handleCourseInteraction(course, 'workbook_downloaded')}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Download Workbook
                          </a>
                        </Button>
                      )}

                      {!course.video_url && !course.workbook_url && (
                        <div className="text-center py-4 text-sm text-muted-foreground">
                          Course materials coming soon
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseLibrary;